export interface PageSEO {
  title: string; // <60 chars
  description: string; // 50-160 chars
  keywords: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
}

// All og:images point to the only real OG asset we ship today.
// Replace per-page with dedicated assets when generated.
const OG = "/images/hero-field-tech.jpg";

export const seoData: Record<string, PageSEO> = {
  home: {
    title: "SimNet Wireless | Field Communications Platform",
    description: "Business internet, failover, fleet tracking, field tablets, push-to-talk, and jobsite connectivity for contractors and field service teams.",
    keywords: ["business internet", "failover", "field communications", "fleet tracking", "jobsite connectivity", "push to talk", "contractor connectivity"],
    ogImage: OG,
  },

  assetTracking: {
    title: "Fleet & Asset Tracking | GPS Fleet Management | SimNet",
    description: "Real-time GPS tracking for vehicles, trailers, tools, and heavy equipment. Geofencing, alerts, recovery tools, and driver insights.",
    keywords: ["fleet tracking", "asset tracking", "GPS fleet management", "vehicle tracking", "OBD GPS tracker", "equipment GPS tracker", "trailer tracking"],
    ogImage: OG,
  },

  fixedWireless: {
    title: "Fixed Wireless Business Internet | SimNet Wireless",
    description: "High-speed fixed wireless internet for businesses. No fiber required. 50–1000 Mbps, fast install, 99.9% uptime SLA. Available nationwide.",
    keywords: ["fixed wireless internet", "business internet", "wireless broadband", "fiber alternative"],
    ogImage: OG,
  },

  failoverSolutions: {
    title: "Business Internet Failover Solutions | SimNet",
    description: "Eliminate downtime with automatic internet failover. Plans from $49–$1,599/mo. Instant switchover, session persistence, 24/7 monitoring.",
    keywords: ["internet failover", "backup internet", "business continuity", "automatic failover", "redundant internet"],
    ogImage: OG,
  },

  starlinkBusiness: {
    title: "Starlink Business Internet | SimNet Wireless",
    description: "Authorized Starlink Business provider. 150–500 Mbps satellite internet anywhere in the US with professional install and enterprise support.",
    keywords: ["Starlink business", "satellite internet", "rural internet", "high-speed satellite"],
    ogImage: OG,
  },

  potsReplacement: {
    title: "POTS Line Replacement | UL2572 & MFVN | SimNet",
    description: "Replace POTS lines before the 2026 copper shutdown. UL2572 & MFVN certified wireless for alarms, elevators, and fire panels. Up to 90% savings.",
    keywords: ["POTS replacement", "analog line replacement", "elevator phone", "fire panel connectivity", "UL2572", "MFVN", "copper shutdown 2026"],
    ogImage: OG,
  },

  iotSolutions: {
    title: "Global IoT Connectivity | Multi-Carrier | SimNet",
    description: "Scalable IoT connectivity across 190+ countries. LTE-M, NB-IoT, Cat-1, and 5G. Centralized management and flexible data plans.",
    keywords: ["IoT connectivity", "M2M solutions", "global IoT", "cellular IoT", "IoT data plans"],
    ogImage: OG,
  },

  homeInternet: {
    title: "Unlimited Home Internet $99/mo | SimNet",
    description: "Unlimited wireless home internet for rural areas. $99/mo flat rate, no data caps, plug-and-play setup, up to 100 Mbps, no credit checks.",
    keywords: ["rural internet", "unlimited home internet", "wireless home internet", "no data caps", "rural broadband"],
    ogImage: OG,
  },

  about: {
    title: "About SimNet Wireless | US-Based Connectivity",
    description: "US-based telecommunications provider delivering enterprise connectivity solutions nationwide for field-based businesses.",
    keywords: ["SimNet wireless", "about SimNet", "telecommunications provider", "enterprise connectivity"],
    ogImage: OG,
  },

  contact: {
    title: "Contact SimNet Wireless | Get a Quote",
    description: "Contact SimNet for enterprise connectivity solutions. Call (512) 859-5047 or request a quote online. US-based support, all 50 states.",
    keywords: ["contact SimNet", "get quote", "business internet quote"],
    ogImage: OG,
  },

  businessCore: {
    title: "Business Core | Essential Internet | SimNet",
    description: "Essential business internet for small operations. 50–250 Mbps, 99.5% uptime SLA, static IP available. From $129/month.",
    keywords: ["business core plan", "small business internet", "essential connectivity", "office internet"],
    ogImage: OG,
  },

  businessAdvanced: {
    title: "Business Advanced | High-Performance Internet",
    description: "Advanced business internet for growing companies. 100–500 Mbps, 99.9% uptime SLA, priority support. From $199/month.",
    keywords: ["business advanced plan", "high-performance internet", "multi-location internet", "priority support"],
    ogImage: OG,
  },

  businessFailover: {
    title: "Business Failover | Automatic Backup | SimNet",
    description: "Business internet with automatic failover. 200–1000 Mbps, instant switchover, session persistence. Mission-critical reliability from $299/mo.",
    keywords: ["business failover plan", "automatic backup internet", "redundant internet", "failover protection"],
    ogImage: OG,
  },

  businessUltimate: {
    title: "Business Ultimate | Max Performance | SimNet",
    description: "Ultimate business connectivity with multi-path failover. 500–1000+ Mbps, 99.99% uptime SLA, dedicated support. From $499/month.",
    keywords: ["business ultimate plan", "enterprise internet", "multi-path failover", "dedicated support"],
    ogImage: OG,
  },

  coreFailover: {
    title: "Core Failover | Essential Backup $49/mo | SimNet",
    description: "Essential failover protection for small businesses. Hot failover, 24/7 monitoring, 1GB backup data. Just $49/month.",
    keywords: ["core failover", "essential failover", "small business backup", "backup internet"],
    ogImage: OG,
  },

  advancedFailover: {
    title: "Advanced Failover | $89/mo | SimNet",
    description: "Advanced failover for growing businesses. Dual-WAN routing, load balancing, 10GB backup data. $89/month.",
    keywords: ["advanced failover", "dual-wan failover", "load balancing", "multi-location failover"],
    ogImage: OG,
  },

  missionCritical: {
    title: "Mission Critical Failover | $549/mo | SimNet",
    description: "Mission-critical failover with multi-carrier redundancy, unlimited backup data, instant switchover. 99.99% uptime SLA. $549/month.",
    keywords: ["mission critical failover", "enterprise failover", "multi-carrier redundancy", "high availability"],
    ogImage: OG,
  },

  enterpriseShield: {
    title: "Enterprise Shield | Maximum Failover | SimNet",
    description: "Ultimate failover with multi-path diversity, SD-WAN integration, and white-glove support. Zero-downtime guarantee. $1,599/month.",
    keywords: ["enterprise shield", "maximum failover", "multi-path failover", "SD-WAN", "zero downtime"],
    ogImage: OG,
  },

  businessInternet: {
    title: "Business Internet Solutions | SimNet Wireless",
    description: "Business internet with fixed wireless, 5G, and hybrid connectivity. Enterprise SLAs, scalable speeds, and US-based support.",
    keywords: ["business internet", "enterprise connectivity", "5G business internet", "hybrid solutions"],
    ogImage: OG,
  },

  privacyPolicy: {
    title: "Privacy Policy | SimNet Wireless",
    description: "SimNet Wireless Privacy Policy. Learn how we protect customer information, handle CPNI, and comply with privacy regulations.",
    keywords: ["privacy policy", "data protection", "CPNI", "customer privacy"],
    ogImage: OG,
  },

  termsOfService: {
    title: "Terms of Service | SimNet Wireless",
    description: "SimNet Wireless Terms of Sale and Service. Review our legal terms, acceptable use policy, and customer responsibilities.",
    keywords: ["terms of service", "user agreement", "acceptable use policy"],
    ogImage: OG,
  },

  notFound: {
    title: "Page Not Found | SimNet Wireless",
    description: "The page you're looking for doesn't exist or may have been moved. Explore our business internet, failover, and connectivity solutions.",
    keywords: ["404 error", "page not found"],
    ogImage: OG,
  },

  becomePartner: {
    title: "Become a SimNet Partner | Reseller Program",
    description: "Join SimNet's partner program. Resell wireless internet, IoT, failover, Starlink, and POTS replacement with recurring revenue.",
    keywords: ["telecommunications reseller", "ISP partner program", "IoT reseller", "telecom reseller"],
    ogImage: OG,
  },

  starlinkNotAvailable: {
    title: "Starlink Not Available? | SimNet Alternative",
    description: "Starlink not available at your address? SimNet provides rural home internet for hard-to-reach locations. Check coverage and try risk-free.",
    keywords: ["starlink not available", "starlink alternative", "rural internet", "rural home internet"],
    ogImage: OG,
  },

  internetOutsideCityLimits: {
    title: "Internet Outside City Limits | SimNet",
    description: "Live outside city limits with no cable or fiber? Our fixed wireless home internet works where traditional providers can't. 14-day trial.",
    keywords: ["internet outside city limits", "rural internet", "country internet", "farm internet", "fixed wireless home internet"],
    ogImage: OG,
  },
};

// Helper function to get SEO data for a page
export const getPageSEO = (pageKey: string): PageSEO => {
  return seoData[pageKey] || seoData.home;
};
